/**
 * SubscriptionsConfigPage - Configuración del flag Pro/Suscripciones para usuarios
 *
 * Equivalente al SubscriptionConfig del admin de la app.
 * Permite activar/desactivar suscripciones y gestionar usuarios de prueba (testers)
 * que pueden ver "Hazte Pro" y suscribirse aunque el flag esté desactivado.
 *
 * Usa app_config: subscriptions_enabled, subscriptions_test_user_ids
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    Crown, Users, Search, X, AlertTriangle, CheckCircle, Loader2,
    ToggleLeft, ToggleRight, CreditCard, Sparkles, Pencil, Check,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConfigItem {
    key: string;
    value: unknown;
}

interface UserSearchResult {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

interface SubscriptionPlanAdmin {
    id: string;
    name: string;
    description: string | null;
    monthly_tokens: number;
    max_rollover_tokens: number;
    tier_level: number;
    sort_order: number;
    is_active: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SubscriptionsConfigPage() {
    const queryClient = useQueryClient();
    const [userSearch, setUserSearch] = useState('');
    const [showUserSearch, setShowUserSearch] = useState(false);
    const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
    const [editDescription, setEditDescription] = useState('');
    const userSearchRef = useRef<HTMLDivElement>(null);

    // ─── Load config (subscriptions_enabled, subscriptions_test_user_ids) ─────
    const { data: config, isLoading } = useQuery({
        queryKey: ['admin', 'subscriptions-config'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<ConfigItem[]>('admin_get_all_config');
            if (error) throw error;
            const items = normalizeRpcResponse(data);
            const map: Record<string, unknown> = {};
            items.forEach((item: ConfigItem) => { map[item.key] = item.value; });

            const parseJson = (val: unknown, def: unknown) => {
                if (val === undefined || val === null) return def;
                if (typeof val === 'string') {
                    try { return JSON.parse(val); } catch { return def; }
                }
                return val;
            };

            return {
                enabled: map['subscriptions_enabled'] === true || map['subscriptions_enabled'] === 'true',
                testUserIds: parseJson(map['subscriptions_test_user_ids'], []) as string[],
            };
        },
    });

    // ─── Load test users info (display_name, email) ────────────────────────────
    const { data: testUsersInfo = [] } = useQuery({
        queryKey: ['admin', 'subscriptions-test-users', config?.testUserIds?.join(',') ?? ''],
        queryFn: async () => {
            const ids = config?.testUserIds ?? [];
            if (ids.length === 0) return [];
            const { data, error } = await dbClient.rpc<UserSearchResult[]>('admin_get_user_profiles_batch', {
                p_user_ids: ids,
            });
            if (error) return [];
            return normalizeRpcResponse(data) as UserSearchResult[];
        },
        enabled: (config?.testUserIds?.length ?? 0) > 0,
    });

    // ─── User search ───────────────────────────────────────────────────────────
    const { data: searchResults = [] } = useQuery({
        queryKey: ['admin', 'search-users-subscriptions', userSearch],
        queryFn: async () => {
            if (!userSearch || userSearch.trim().length < 2) return [];
            const { data, error } = await dbClient.rpc<UserSearchResult[]>('admin_search_users', {
                p_search_query: userSearch.trim(),
                p_search_by_email: true,
                p_result_limit: 10,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as UserSearchResult[];
        },
        enabled: userSearch.trim().length >= 2,
    });

    const filteredSearchResults = searchResults.filter(
        u => !config?.testUserIds?.includes(u.user_id)
    );

    // ─── Load subscription plans ─────────────────────────────────────────────
    const { data: subscriptionPlans = [], isLoading: plansLoading } = useQuery({
        queryKey: ['admin', 'subscription-plans'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SubscriptionPlanAdmin[]>('admin_get_subscription_plans');
            if (error) throw error;
            const arr = normalizeRpcResponse(data) as SubscriptionPlanAdmin[];
            return arr.map((row) => ({
                id: row.id,
                name: row.name,
                description: row.description ?? null,
                monthly_tokens: row.monthly_tokens ?? 0,
                max_rollover_tokens: row.max_rollover_tokens ?? 0,
                tier_level: row.tier_level ?? 0,
                sort_order: row.sort_order ?? 0,
                is_active: row.is_active ?? true,
            }));
        },
    });

    // ─── Mutations ────────────────────────────────────────────────────────────
    const toggleMutation = useMutation({
        mutationFn: async (enabled: boolean) => {
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'subscriptions_enabled',
                p_value: enabled,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions-config'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
        },
    });

    const addTestUserMutation = useMutation({
        mutationFn: async (userId: string) => {
            const current = config?.testUserIds ?? [];
            if (current.includes(userId)) throw new Error('Usuario ya en la lista');
            const next = [...current, userId];
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'subscriptions_test_user_ids',
                p_value: next,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions-config'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
            setUserSearch('');
            setShowUserSearch(false);
        },
    });

    const removeTestUserMutation = useMutation({
        mutationFn: async (userId: string) => {
            const current = config?.testUserIds ?? [];
            const next = current.filter(id => id !== userId);
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'subscriptions_test_user_ids',
                p_value: next,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions-config'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
        },
    });

    const togglePlanMutation = useMutation({
        mutationFn: async ({ planId, isActive }: { planId: string; isActive: boolean }) => {
            const { error } = await dbClient.rpc('admin_toggle_subscription_plan', {
                p_plan_id: planId,
                p_is_active: isActive,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscription-plans'] });
        },
    });

    const updatePlanMutation = useMutation({
        mutationFn: async ({ planId, description }: { planId: string; description?: string }) => {
            const { error } = await dbClient.rpc('admin_update_subscription_plan', {
                p_plan_id: planId,
                p_description: description ?? undefined,
            });
            if (error) throw error;
        },
        onSuccess: (_, { planId }) => {
            setEditingPlanId(null);
            setEditDescription('');
            queryClient.invalidateQueries({ queryKey: ['admin', 'subscription-plans'] });
        },
    });

    // Close dropdown on click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
                setShowUserSearch(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Config Pro / Suscripciones"
                description="Activa el sistema de suscripciones para todos los usuarios o solo para testers seleccionados."
            />

            {/* Estado del sistema */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-amber-500" />
                        <h3 className="font-semibold">Suscripciones habilitadas</h3>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                        <div>
                            <p className="text-sm font-medium">
                                {config?.enabled ? 'Todos pueden suscribirse' : 'Solo testers pueden suscribirse'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {config?.enabled
                                    ? 'Los usuarios verán "Hazte Pro" y el modal de suscripción'
                                    : 'Solo los usuarios de la lista verán la opción Pro'}
                            </p>
                        </div>
                        <button
                            onClick={() => toggleMutation.mutate(!config?.enabled)}
                            disabled={toggleMutation.isPending}
                            className={cn(
                                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                config?.enabled
                                    ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            )}
                        >
                            {toggleMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : config?.enabled ? (
                                <><ToggleRight size={18} /> Desactivar</>
                            ) : (
                                <><ToggleLeft size={18} /> Activar</>
                            )}
                        </button>
                    </div>
                    <div
                        className={cn(
                            'flex items-center gap-3 p-4 rounded-lg',
                            config?.enabled
                                ? 'bg-emerald-500/10 border border-emerald-500/20'
                                : 'bg-amber-500/10 border border-amber-500/20'
                        )}
                    >
                        {config?.enabled ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                                <p className="text-sm">Pro visible para todos los usuarios en el perfil.</p>
                            </>
                        ) : (
                            <>
                                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                <p className="text-sm">Modo prueba: solo los usuarios de la lista pueden ver Pro y suscribirse.</p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Planes de suscripción */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Planes de Suscripción</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Habilita o deshabilita planes. Solo los habilitados aparecen para que los usuarios puedan suscribirse.
                    </p>
                    {plansLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : subscriptionPlans.length > 0 ? (
                        <div className="space-y-3">
                            {subscriptionPlans.map((plan) => (
                                <div
                                    key={plan.id}
                                    className={cn(
                                        'flex flex-col gap-3 p-4 rounded-lg border',
                                        plan.is_active ? 'bg-muted/30 border-primary/20' : 'bg-muted/10 border-muted opacity-75'
                                    )}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {plan.id === 'premium' ? (
                                                <Crown className="w-5 h-5 text-amber-500" />
                                            ) : (
                                                <Sparkles className="w-5 h-5 text-blue-500" />
                                            )}
                                            <div>
                                                <p className="font-medium">{plan.name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {plan.monthly_tokens} tokens/mes · Rollover {plan.max_rollover_tokens}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => togglePlanMutation.mutate({ planId: plan.id, isActive: !plan.is_active })}
                                            disabled={togglePlanMutation.isPending}
                                            className={cn(
                                                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                                                plan.is_active
                                                    ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                            )}
                                            title={plan.is_active ? 'Deshabilitar (ocultar a usuarios)' : 'Habilitar (mostrar a usuarios)'}
                                        >
                                            {togglePlanMutation.isPending ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : plan.is_active ? (
                                                <><ToggleRight size={16} /> Deshabilitar</>
                                            ) : (
                                                <><ToggleLeft size={16} /> Habilitar</>
                                            )}
                                        </button>
                                    </div>
                                    <div className="border-t pt-3 space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-medium text-muted-foreground">Descripción (visible en modal de suscripción)</span>
                                            {editingPlanId === plan.id ? (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => updatePlanMutation.mutate({ planId: plan.id, description: editDescription })}
                                                        disabled={updatePlanMutation.isPending || editDescription === (plan.description || '')}
                                                        className="p-1.5 rounded-md hover:bg-muted"
                                                    >
                                                        {updatePlanMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check size={14} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setEditingPlanId(null); setEditDescription(''); }}
                                                        className="p-1.5 rounded-md hover:bg-muted"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => { setEditingPlanId(plan.id); setEditDescription(plan.description || ''); }}
                                                    className="p-1.5 rounded-md hover:bg-muted"
                                                    title="Editar descripción"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                            )}
                                        </div>
                                        {editingPlanId === plan.id ? (
                                            <textarea
                                                value={editDescription}
                                                onChange={(e) => setEditDescription(e.target.value)}
                                                className="w-full min-h-[60px] px-3 py-2 text-sm rounded-md border border-border bg-background"
                                                placeholder="Descripción del plan..."
                                                autoFocus
                                            />
                                        ) : (
                                            <p className="text-sm text-foreground/80">
                                                {plan.description || <span className="italic text-muted-foreground">Sin descripción</span>}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground text-center py-6">
                            No hay planes configurados o no tienes permisos de administrador.
                        </p>
                    )}
                </div>
            </div>

            {/* Testers */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Usuarios de prueba (testers)</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Añade usuarios que puedan ver y usar Pro cuando las suscripciones están desactivadas.
                    </p>

                    {/* Search + add */}
                    <div className="relative" ref={userSearchRef}>
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Buscar por email o nombre (min. 2 caracteres)..."
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            onFocus={() => setShowUserSearch(true)}
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
                        />
                        {showUserSearch && filteredSearchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg z-10 max-h-60 overflow-y-auto">
                                {filteredSearchResults.map(u => (
                                    <button
                                        key={u.user_id}
                                        type="button"
                                        onClick={() => addTestUserMutation.mutate(u.user_id)}
                                        disabled={addTestUserMutation.isPending}
                                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                                    >
                                        <span className="text-sm font-medium truncate">
                                            {u.display_name || u.email || u.user_id}
                                        </span>
                                        <span className="text-xs text-muted-foreground truncate">
                                            {u.email || u.user_id}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Lista de testers */}
                    {testUsersInfo.length > 0 ? (
                        <ul className="space-y-2">
                            {testUsersInfo.map(u => (
                                <li
                                    key={u.user_id}
                                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                                >
                                    <div>
                                        <p className="text-sm font-medium">
                                            {u.display_name || u.email || 'Sin nombre'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{u.email || u.user_id}</p>
                                    </div>
                                    <button
                                        onClick={() => removeTestUserMutation.mutate(u.user_id)}
                                        disabled={removeTestUserMutation.isPending}
                                        className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                        title="Quitar de testers"
                                    >
                                        <X size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground py-4">
                            No hay usuarios de prueba. Añade alguno para que puedan ver Pro cuando está desactivado.
                        </p>
                    )}
                </div>
            </div>

            {/* Stripe Dashboard */}
            <div className="rounded-xl border border-dashed border-border bg-card overflow-hidden">
                <div className="flex items-center gap-4 p-4">
                    <CreditCard className="w-8 h-8 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="font-medium">Stripe Dashboard</p>
                        <p className="text-sm text-muted-foreground">
                            Gestiona productos, precios y suscripciones directamente en Stripe
                        </p>
                    </div>
                    <a
                        href="https://dashboard.stripe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 px-4 py-2 rounded-lg border border-border bg-background text-sm font-medium hover:bg-muted transition-colors"
                    >
                        Abrir Stripe
                    </a>
                </div>
            </div>
        </div>
    );
}
